import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Plus,
  PencilSimple,
  Trash,
  Check,
  X,
  Columns,
  UsersThree,
  CircleNotch,
  Key,
} from "@phosphor-icons/react";
import { adminApi, getErrorMessage } from "@/lib/api";
import { AdminHeader, PrimaryButton, GhostButton, Field, inputCls } from "@/components/admin/parts";

const EMPTY_FORM = { id: null, username: "", password: "", name: "", dealers: [], active: true };

export default function AdminDealerPortal() {
  const [loading, setLoading] = useState(true);
  const [boardCols, setBoardCols] = useState([]);
  const [selectedCols, setSelectedCols] = useState([]); // array of ids (ordered by board)
  const [savingCols, setSavingCols] = useState(false);
  const [users, setUsers] = useState([]);
  const [dealerOptions, setDealerOptions] = useState([]);
  const [form, setForm] = useState(null); // null = closed
  const [saving, setSaving] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cols, cfg, us, opts] = await Promise.all([
        adminApi.get("/admin/board-columns"),
        adminApi.get("/admin/dealer-portal-config"),
        adminApi.get("/admin/dealer-users"),
        adminApi.get("/admin/dealer-options"),
      ]);
      setBoardCols(cols.data.columns || []);
      setSelectedCols((cfg.data.columns || []).map((c) => c.id));
      setUsers(us.data || []);
      setDealerOptions(opts.data.dealers || []);
    } catch (err) {
      toast.error(getErrorMessage(err, "Couldn't load the dealer portal settings"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const toggleCol = (id) => {
    setSelectedCols((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };

  const saveColumns = async () => {
    if (selectedCols.length === 0) { toast.error("Select at least one column"); return; }
    setSavingCols(true);
    try {
      // preserve board order
      const ordered = boardCols.filter((c) => selectedCols.includes(c.id));
      await adminApi.put("/admin/dealer-portal-config", { columns: ordered });
      toast.success("Visible columns saved");
    } catch (err) {
      toast.error(getErrorMessage(err, "Couldn't save columns"));
    } finally {
      setSavingCols(false);
    }
  };

  const openCreate = () => setForm({ ...EMPTY_FORM });
  const openEdit = (u) => setForm({ id: u.id, username: u.username, password: "", name: u.name || "", dealers: u.dealers || [], active: u.active !== false });

  const toggleDealer = (d) => {
    setForm((f) => ({ ...f, dealers: f.dealers.includes(d) ? f.dealers.filter((x) => x !== d) : [...f.dealers, d] }));
  };

  const saveUser = async () => {
    if (!form.username.trim()) { toast.error("Username is required"); return; }
    if (!form.id && (!form.password || form.password.length < 4)) { toast.error("Set a password (min 4 characters)"); return; }
    setSaving(true);
    try {
      if (form.id) {
        const payload = { name: form.name, dealers: form.dealers, active: form.active };
        if (form.password) payload.password = form.password;
        await adminApi.patch(`/admin/dealer-users/${form.id}`, payload);
        toast.success("Dealer account updated");
      } else {
        await adminApi.post("/admin/dealer-users", {
          username: form.username, password: form.password, name: form.name, dealers: form.dealers, active: form.active,
        });
        toast.success("Dealer account created");
      }
      setForm(null);
      loadAll();
    } catch (err) {
      toast.error(getErrorMessage(err, "Couldn't save the dealer account"));
    } finally {
      setSaving(false);
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete dealer account "${u.username}"? This cannot be undone.`)) return;
    try {
      await adminApi.delete(`/admin/dealer-users/${u.id}`);
      toast.success("Dealer account deleted");
      loadAll();
    } catch (err) {
      toast.error(getErrorMessage(err, "Couldn't delete the account"));
    }
  };

  return (
    <div data-testid="admin-dealer-portal">
      <AdminHeader
        title="Dealer Portal"
        subtitle="Create dealer logins, choose which Selling Dealer claims each account sees, and pick the columns shown in the portal."
      />

      <div className="px-8 py-6 space-y-8 max-w-5xl">
        {loading ? (
          <div className="flex items-center gap-2 text-neutral-500 py-10"><CircleNotch size={18} className="animate-spin" /> Loading…</div>
        ) : (
          <>
            {/* Visible columns */}
            <section className="bg-[#0F0F0F] border border-neutral-800 rounded-2xl p-6">
              <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-[#73bf44]/10 flex items-center justify-center">
                    <Columns size={18} className="text-[#73bf44]" weight="duotone" />
                  </div>
                  <div>
                    <h3 className="font-heading text-sm font-bold text-white">Visible Columns</h3>
                    <p className="text-xs text-neutral-500">Shared by all dealer logins · {selectedCols.length} selected</p>
                  </div>
                </div>
                <PrimaryButton onClick={saveColumns} disabled={savingCols} data-testid="save-columns-btn">
                  {savingCols ? <CircleNotch size={16} className="animate-spin" /> : <Check size={16} />} Save Columns
                </PrimaryButton>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {boardCols.map((c) => {
                  const on = selectedCols.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      onClick={() => toggleCol(c.id)}
                      data-testid={`col-toggle-${c.id}`}
                      className={`flex items-center gap-2.5 text-left rounded-lg border px-3 py-2.5 text-sm transition-colors ${
                        on ? "border-[#73bf44]/50 bg-[#73bf44]/10 text-white" : "border-neutral-800 text-neutral-400 hover:border-neutral-700"
                      }`}
                    >
                      <span className={`w-4 h-4 rounded flex items-center justify-center shrink-0 border ${on ? "bg-[#73bf44] border-[#73bf44]" : "border-neutral-600"}`}>
                        {on && <Check size={11} weight="bold" className="text-[#050505]" />}
                      </span>
                      <span className="truncate">{c.title}</span>
                    </button>
                  );
                })}
              </div>
            </section>

            {/* Dealer accounts */}
            <section className="bg-[#0F0F0F] border border-neutral-800 rounded-2xl p-6">
              <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-[#73bf44]/10 flex items-center justify-center">
                    <UsersThree size={18} className="text-[#73bf44]" weight="duotone" />
                  </div>
                  <div>
                    <h3 className="font-heading text-sm font-bold text-white">Dealer Accounts</h3>
                    <p className="text-xs text-neutral-500">{users.length} account{users.length === 1 ? "" : "s"}</p>
                  </div>
                </div>
                <PrimaryButton onClick={openCreate} data-testid="add-dealer-btn"><Plus size={16} /> Add Dealer</PrimaryButton>
              </div>

              {users.length === 0 ? (
                <p className="text-sm text-neutral-500 py-6 text-center">No dealer accounts yet. Add one to give a dealership access.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="dealer-users-table">
                    <thead>
                      <tr className="border-b border-neutral-800 text-[0.7rem] uppercase tracking-[0.1em] text-neutral-500">
                        <th className="text-left font-semibold px-3 py-2.5">Username</th>
                        <th className="text-left font-semibold px-3 py-2.5">Name</th>
                        <th className="text-left font-semibold px-3 py-2.5">Sees dealers</th>
                        <th className="text-left font-semibold px-3 py-2.5">Status</th>
                        <th className="text-right font-semibold px-3 py-2.5">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <tr key={u.id} data-testid={`dealer-user-${u.username}`} className="border-b border-neutral-900 last:border-0">
                          <td className="px-3 py-3 font-mono text-[#73bf44]">{u.username}</td>
                          <td className="px-3 py-3 text-neutral-200">{u.name || "—"}</td>
                          <td className="px-3 py-3 text-neutral-400 max-w-[20rem]">
                            <div className="flex flex-wrap gap-1">
                              {(u.dealers || []).length === 0 ? <span className="text-neutral-600">None</span> :
                                (u.dealers || []).map((d) => (
                                  <span key={d} className="text-[0.65rem] bg-neutral-800/70 border border-neutral-700 rounded-full px-2 py-0.5">{d}</span>
                                ))}
                            </div>
                          </td>
                          <td className="px-3 py-3">
                            <span className={`inline-flex items-center gap-1.5 text-xs ${u.active !== false ? "text-[#73bf44]" : "text-neutral-500"}`}>
                              <span className={`w-2 h-2 rounded-full ${u.active !== false ? "bg-[#73bf44]" : "bg-neutral-600"}`} />
                              {u.active !== false ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td className="px-3 py-3">
                            <div className="flex items-center justify-end gap-1">
                              <button onClick={() => openEdit(u)} data-testid={`edit-dealer-${u.username}`} className="p-2 rounded-lg text-neutral-400 hover:text-white hover:bg-white/5 transition-colors"><PencilSimple size={16} /></button>
                              <button onClick={() => deleteUser(u)} data-testid={`delete-dealer-${u.username}`} className="p-2 rounded-lg text-red-400 hover:bg-red-500/10 transition-colors"><Trash size={16} /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}
      </div>

      {/* Create / edit modal */}
      {form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => !saving && setForm(null)}>
          <div className="bg-[#0F0F0F] border border-neutral-800 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="dealer-form-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-800 sticky top-0 bg-[#0F0F0F]">
              <h3 className="font-heading font-bold text-white">{form.id ? "Edit dealer account" : "New dealer account"}</h3>
              <button onClick={() => setForm(null)} className="text-neutral-500 hover:text-white"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              <Field label="Username">
                <input
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  disabled={!!form.id}
                  data-testid="dealer-form-username"
                  placeholder="e.g. 4krv"
                  className={`${inputCls} ${form.id ? "opacity-60 cursor-not-allowed" : ""}`}
                />
              </Field>
              <Field label="Display name">
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="dealer-form-name" placeholder="e.g. RV Centre WA" className={inputCls} />
              </Field>
              <Field label={form.id ? "Reset password (leave blank to keep)" : "Password"} hint={form.id ? "Only fill this to set a new password." : "Minimum 4 characters."}>
                <div className="flex items-center gap-2 bg-black/40 border border-neutral-700 rounded-lg px-3 focus-within:border-[#73bf44] transition-colors">
                  <Key size={15} className="text-neutral-500" />
                  <input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="dealer-form-password" placeholder={form.id ? "••••••" : "Set a password"} className="w-full bg-transparent py-2.5 text-sm text-white outline-none placeholder:text-neutral-600" />
                </div>
              </Field>
              <Field label="Can see claims for these Selling Dealers">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
                  {dealerOptions.map((d) => {
                    const on = form.dealers.includes(d);
                    return (
                      <button
                        key={d}
                        onClick={() => toggleDealer(d)}
                        data-testid={`dealer-opt-${d}`}
                        className={`flex items-center gap-2 text-left rounded-lg border px-3 py-2 text-sm transition-colors ${
                          on ? "border-[#73bf44]/50 bg-[#73bf44]/10 text-white" : "border-neutral-800 text-neutral-400 hover:border-neutral-700"
                        }`}
                      >
                        <span className={`w-4 h-4 rounded flex items-center justify-center shrink-0 border ${on ? "bg-[#73bf44] border-[#73bf44]" : "border-neutral-600"}`}>
                          {on && <Check size={11} weight="bold" className="text-[#050505]" />}
                        </span>
                        <span className="truncate">{d}</span>
                      </button>
                    );
                  })}
                </div>
              </Field>
              <label className="flex items-center gap-2.5 text-sm text-neutral-300 cursor-pointer">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="dealer-form-active" className="w-4 h-4 accent-[#73bf44]" />
                Account active (can log in)
              </label>
            </div>
            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-neutral-800 sticky bottom-0 bg-[#0F0F0F]">
              <GhostButton onClick={() => setForm(null)} disabled={saving}>Cancel</GhostButton>
              <PrimaryButton onClick={saveUser} disabled={saving} data-testid="dealer-form-save">
                {saving ? <CircleNotch size={16} className="animate-spin" /> : <Check size={16} />} {form.id ? "Save changes" : "Create account"}
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
